import React, { useEffect, useId, useRef, useState } from 'react';
import { PhotoIcon } from "@heroicons/react/24/solid";
import ActionButton from './ActionButton';
import styles from './Form.module.css';

interface SimpleFileUploadProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  required?: boolean;
  fileNote?: string;
  value?: File | File[] | null;
  onChange: (files: File | File[] | null) => void;
  error?: string;
}

const SimpleFileUpload: React.FC<SimpleFileUploadProps> = ({
  label,
  accept = '*',
  multiple = false,
  required = false,
  fileNote = 'Upload your files',
  value,
  onChange,
  error
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const inputId = useId();

  // Update previews when value changes
  useEffect(() => {
    const objectUrls: string[] = [];

    if (value) {
      if (Array.isArray(value)) {
        const newPreviews = value.map(file => {
          const previewUrl = URL.createObjectURL(file);
          objectUrls.push(previewUrl);
          return previewUrl;
        });
        setPreviews(newPreviews);
      } else if (value instanceof File) {
        const previewUrl = URL.createObjectURL(value);
        objectUrls.push(previewUrl);
        setPreviews([previewUrl]);
      }
    } else {
      setPreviews([]);
    }

    return () => {
      objectUrls.forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
      });
    };
  }, [value]);

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    if (multiple) {
      onChange(fileArray);
    } else {
      onChange(fileArray[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileChange(e.dataTransfer.files);
  };

  const handleRemove = (index: number) => {
    if (multiple && Array.isArray(value)) {
      const newFiles = value.filter((_, i) => i !== index);
      onChange(newFiles);
    } else {
      onChange(null);
    }
  };


  return (
    <div className={styles.formGroup}>
      <div className={styles.formLabel}>
        {label}
        {required && <span className={styles.formRequired}>*</span>}
      </div>
      
      <div 
        style={{ 
          border: '2px dashed #ccc', 
          padding: '20px', 
          textAlign: 'center',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          position: 'relative'
        }}
        onDragEnter={() => setIsDragOver(true)}
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <PhotoIcon style={{ 
          width: '3rem', 
          height: '3rem', 
          color: '#6b7280', 
          marginBottom: '0.5rem' 
        }} />
        
        <p style={{ margin: '10px 0', color: '#666' }}>
          Drag and drop your files here or click to browse
        </p>
        
        {/* File input - hidden but accessible */}
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            handleFileChange(e.target.files);
          }}
          style={{ 
            display: 'none'
          }}
        />
        
        {/* Browse button - Using button with direct click handling */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            inputRef.current?.click();
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6b7280',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            marginTop: '10px',
            display: 'inline-block'
          }}
        >
          Browse Files
        </button>
        
        <p style={{ 
          fontSize: '0.8rem', 
          color: '#666', 
          marginTop: '10px' 
        }}>
          {fileNote}
        </p>
      </div>

      {/* File previews */}
      {previews.length > 0 && (
        <div className={styles.filePreviews}>
          {previews.map((preview, index) => (
            <div key={index} className={styles.filePreview} style={{ position: 'relative' }}>
              {accept.includes('image') ? (
                <img
                  src={preview}
                  alt={`Preview ${index}`}
                  className={styles.filePreviewImg}
                  width={80}
                  height={80}
                />
              ) : accept.includes('video') ? (
                <video
                  src={preview}
                  className={styles.filePreviewVideo}
                  controls
                />
              ) : (
                <div className={styles.filePreviewGeneric}>
                  {Array.isArray(value) ? (value[index] as File)?.name : (value as File)?.name}
                </div>
              )}
              <ActionButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove(index);
                }}
                variant="danger"
                size="small"
                aria-label="Remove file"
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  zIndex: 1000
                }}
              >
                ×
              </ActionButton>
            </div>
          ))}
        </div>
      )}
      
      {error && <p className={styles.formErrorMessage} role="alert">{error}</p>}
    </div>
  );
};

export default SimpleFileUpload;
